import React, { useEffect, useState } from "react";
import style from "./Monetization.module.css";
import { showToast } from "../../utils/Toastify/Toastify";
import { getMonetizationSettings, updateMonetizationSettings } from "../../api/HttpApis";

const MODELS = [
  { value: "1_month", label: "1 Month" },
  { value: "2_month", label: "2 Months" },
  { value: "lifetime", label: "Lifetime" },
];

// Money percentages that must always have a value when saving (see handleSave).
const PERCENT_FIELDS = [
  { key: "watch_pool_percent", label: "Watch Pool Percentage" },
  { key: "default_affiliate_percent", label: "Default Affiliate Percentage" },
  { key: "tvod_creator_percent", label: "TVOD Creator Share" },
];

const NUMBER_FIELDS = [
  "referral_attribution_days",
  "watch_min_segment_seconds",
  "watch_idle_timeout_seconds",
  "default_max_devices",
  "stale_session_seconds",
  "default_payout_threshold_cents",
  "payout_hold_period_days",
];
const DECIMAL_FIELDS = ["default_affiliate_percent", "watch_pool_percent"];

// Advanced: fraud review sensitivity.
//
// These decide WHICH accounts get surfaced to a human reviewer. They do not
// block playback and they do not remove anyone's watch-time from a payout —
// that is the separate "Enforce watch-fraud exclusions" switch below, which
// applies only to the temporal-impossibility rule.
//
// Kept in a collapsed section on purpose: an admin doing everyday work
// (changing the watch pool, the device limit) should never have to read past
// them. `min`/`max` mirror FRAUD_THRESHOLD_BOUNDS on the backend, which
// rejects out-of-range values regardless of what the browser allows.
const FRAUD_INT_FIELDS = [
  "fraud_device_churn_threshold",
  "fraud_device_churn_fp_min_devices",
  "fraud_repeat_watch_threshold",
  "fraud_shared_ip_min_accounts",
  "fraud_daily_ceiling_hours_per_device",
  "fraud_seek_min_suspicious_pairs",
];
const FRAUD_RATIO_FIELDS = ["fraud_vpn_ratio_threshold", "fraud_concentration_creator_ratio"];

// Labels are short on purpose — a reviewer scans eight of these at once, and
// a label that has to be parsed as a sentence ("Believable streaming hours per
// device per day") stops being a label. The one-line help says what a flag
// means; the detail lives in the FAQ on the review page, not here.
// Written for someone who does not work on this system. No "device IDs", no
// "sessions", no "datacenter" — each line says, in the words a person would
// actually use, what makes an account show up in the review list.
const FRAUD_FIELDS = [
  { key: "fraud_device_churn_threshold", label: "New devices a day",
    min: 2, max: 100, step: 1, def: 5, help: "More new devices than this in a day." },
  { key: "fraud_device_churn_fp_min_devices", label: "Fake devices from one computer",
    min: 2, max: 50, step: 1, def: 4, help: "One computer posing as this many." },
  { key: "fraud_repeat_watch_threshold", label: "Times one video is rewatched",
    min: 2, max: 100, step: 1, def: 5, help: "Same video finished this often in a day." },
  { key: "fraud_shared_ip_min_accounts", label: "Accounts on one internet line",
    min: 2, max: 200, step: 1, def: 5, help: "This many accounts in one home or office." },
  { key: "fraud_daily_ceiling_hours_per_device", label: "Hours one device can watch a day",
    min: 4, max: 24, step: 1, def: 16, help: "More than this is not believable." },
  { key: "fraud_seek_min_suspicious_pairs", label: "Impossible jumps in a video",
    min: 2, max: 100, step: 1, def: 5, help: "Skipping faster than it can play." },
  { key: "fraud_vpn_ratio_threshold", label: "How much viewing hides its location",
    min: 0.5, max: 1, step: 0.05, def: 0.9, help: "0.9 = 9 views out of 10." },
  { key: "fraud_concentration_creator_ratio", label: "Creator's views from odd accounts",
    min: 0.1, max: 1, step: 0.05, def: 0.5, help: "0.5 = half their views." },
];

export default function MonetizationSettings() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Collapsed by default — everyday settings work should never involve
  // scrolling past eight fraud thresholds.
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await getMonetizationSettings();
        // Backend envelope: { status, data: {...settings} }
        setForm(res?.data ?? res ?? {});
      } catch (err) {
        showToast(err?.response?.data?.message || "Failed to load settings", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    // Money percentages are never sent blank: Number("") is 0, which the API
    // accepts as a valid rate, so a cleared box would silently set creators'
    // share to 0%. Stop here instead of saving.
    const blankPercent = PERCENT_FIELDS.find(
      ({ key }) => form[key] === undefined || form[key] === null || String(form[key]).trim() === ""
    );
    if (blankPercent) {
      showToast(`${blankPercent.label} can't be empty. Enter a value from 0 to 100.`, "error");
      return;
    }
    // These rates are read when the revenue engine RUNS, not continuously, so a
    // saved change does nothing to figures already on screen until the engine is
    // re-run for a period. Saying so here prevents the "I changed it to 50% but
    // the dashboard still says 10%" confusion — which is exactly what happened:
    // the pool really was 50%, an older run had simply been calculated at 10%.
    const proceed = window.confirm(
      "Save monetization settings?\n\n" +
        "These rates apply the next time the revenue engine runs. Earnings and " +
        "payouts already calculated keep the rate they were calculated with, " +
        "and the dashboard will keep showing those figures until you re-run the " +
        "engine for that period.\n\n" +
        "TVOD share changes only apply to sales not yet booked. Sales already " +
        "booked keep their original share, even if the period is re-run."
    );
    if (!proceed) return;
    setSaving(true);
    try {
      const payload = {
        enable_monetization: !!form.enable_monetization,
        default_affiliate_model: form.default_affiliate_model,
        default_affiliate_percent: Number(form.default_affiliate_percent),
        watch_pool_percent: Number(form.watch_pool_percent),
        tvod_creator_percent: Number(form.tvod_creator_percent),
        payout_currency: form.payout_currency,
        cap_watch_at_duration: !!form.cap_watch_at_duration,
        payout_confirmation_email_enabled: !!form.payout_confirmation_email_enabled,
        // The dropdown is the single source of truth. The older true/false
        // key is kept in step with it so the two can never disagree — two
        // switches for one decision is how "why didn't it exclude?" happens.
        watch_fraud_auto_exclude_mode: form.watch_fraud_auto_exclude_mode || "shadow",
        watch_fraud_enforcement_enabled: (form.watch_fraud_auto_exclude_mode || "shadow") === "enforce",
      };
      NUMBER_FIELDS.forEach((f) => {
        if (form[f] !== undefined && form[f] !== "") payload[f] = parseInt(form[f], 10);
      });
      // Fraud thresholds. A blank field is omitted rather than sent as 0 —
      // the backend then keeps whatever is stored, and a never-configured
      // column stays null so the rule keeps using its built-in default.
      FRAUD_INT_FIELDS.forEach((f) => {
        if (form[f] !== undefined && form[f] !== "" && form[f] !== null) payload[f] = parseInt(form[f], 10);
      });
      FRAUD_RATIO_FIELDS.forEach((f) => {
        if (form[f] !== undefined && form[f] !== "" && form[f] !== null) payload[f] = Number(form[f]);
      });
      await updateMonetizationSettings(payload);
      showToast("Settings saved", "success");
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className={style.page}><div className={style.loading}>Loading...</div></div>;
  if (!form) return <div className={style.page}><div className={style.empty}>No settings found.</div></div>;

  return (
    <div className={style.page}>
      <div className={style.headerRow}>
        <div>
          <h2 className={style.title}>Monetization Settings</h2>
          <p className={style.subtitle}>Platform-wide defaults for creator earnings, watch pool, and device limits.</p>
        </div>
      </div>

      <div className={style.form}>
        <div className={style.formGrid}>
          <div className={style.formGroup}>
            <label>Watch Pool Percentage (%)</label>
            <input className={style.input} type="number" step="0.01" min="0" max="100"
              value={form.watch_pool_percent ?? ""} onChange={(e) => set("watch_pool_percent", e.target.value)} />
            <span className={style.helper}>Share of net subscription revenue put into the monthly watch pool.</span>
          </div>

          <div className={style.formGroup}>
            <label>Default Affiliate Percentage (%)</label>
            <input className={style.input} type="number" step="0.01" min="0" max="100"
              value={form.default_affiliate_percent ?? ""} onChange={(e) => set("default_affiliate_percent", e.target.value)} />
            <span className={style.helper}>Default referral share for new creators (override per creator).</span>
          </div>

          <div className={style.formGroup}>
            <label>TVOD Creator Share (%)</label>
            <input className={style.input} type="number" step="0.01" min="0" max="100"
              value={form.tvod_creator_percent ?? ""} onChange={(e) => set("tvod_creator_percent", e.target.value)} />
            <span className={style.helper}>
              Share of each buy/rent sale paid to the title's creator, taken after Stripe's fee.
            </span>
          </div>

          <div className={style.formGroup}>
            <label>Default Affiliate Model</label>
            <select className={style.select} value={form.default_affiliate_model || "lifetime"}
              onChange={(e) => set("default_affiliate_model", e.target.value)}>
              {MODELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>

          <div className={style.formGroup}>
            <label>Referral Attribution Window (days)</label>
            <input className={style.input} type="number" min="1"
              value={form.referral_attribution_days ?? ""} onChange={(e) => set("referral_attribution_days", e.target.value)} />
            <span className={style.helper}>How long a click counts toward a creator before signup.</span>
          </div>

          <div className={style.formGroup}>
            <label>Default Max Devices</label>
            <input className={style.input} type="number" min="1"
              value={form.default_max_devices ?? ""} onChange={(e) => set("default_max_devices", e.target.value)} />
            <span className={style.helper}>Simultaneous streams when the plan has no device limit set.</span>
          </div>

          <div className={style.formGroup}>
            <label>Stale Session Timeout (seconds)</label>
            <input className={style.input} type="number" min="30"
              value={form.stale_session_seconds ?? ""} onChange={(e) => set("stale_session_seconds", e.target.value)} />
            <span className={style.helper}>No-heartbeat time before a device slot is freed.</span>
          </div>

          <div className={style.formGroup}>
            <label>Min Watch Segment (seconds)</label>
            <input className={style.input} type="number" min="0"
              value={form.watch_min_segment_seconds ?? ""} onChange={(e) => set("watch_min_segment_seconds", e.target.value)} />
            <span className={style.helper}>Ignore watch bursts shorter than this (anti-gaming).</span>
          </div>

          <div className={style.formGroup}>
            <label>Payout Currency</label>
            <input className={style.input} type="text" maxLength="3"
              value={form.payout_currency || "USD"} onChange={(e) => set("payout_currency", e.target.value.toUpperCase())} />
          </div>

          <div className={style.formGroup}>
            <label>Minimum Payout Amount ($)</label>
            {/* Stored in cents; shown in dollars because that's how this is
                actually decided. Without this input the minimum was
                unreachable from the UI and silently stayed at $0. */}
            <input className={style.input} type="number" min="0" step="1"
              value={
                form.default_payout_threshold_cents === "" || form.default_payout_threshold_cents == null
                  ? ""
                  : Number(form.default_payout_threshold_cents) / 100
              }
              onChange={(e) =>
                set(
                  "default_payout_threshold_cents",
                  e.target.value === "" ? "" : Math.round(Number(e.target.value) * 100)
                )
              } />
            <span className={style.helper}>
              Creators below this keep accumulating instead of being paid. Their money is never lost — it carries to the next period.
            </span>
          </div>

          <div className={style.formGroup}>
            <label>Payout Hold Period (days)</label>
            <input className={style.input} type="number" min="0"
              value={form.payout_hold_period_days ?? ""} onChange={(e) => set("payout_hold_period_days", e.target.value)} />
            <span className={style.helper}>
              Days after a month ends before its earnings can be paid, so most refunds land before money leaves. 0 = pay as soon as the month closes.
            </span>
          </div>
        </div>

        <div className={style.formGroup} style={{ marginTop: 18 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={!!form.payout_confirmation_email_enabled}
              onChange={(e) => set("payout_confirmation_email_enabled", e.target.checked)} />
            Email creators when they get paid
          </label>
          <span className={style.helper} style={{ marginLeft: 24, display: "block" }}>
            Sends a receipt with the earnings breakdown (Watch Pool / Affiliate / TVOD) whenever a payout is sent — via Stripe or marked paid manually.
          </span>
        </div>

        <div className={style.formGroup} style={{ marginTop: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={!!form.cap_watch_at_duration}
              onChange={(e) => set("cap_watch_at_duration", e.target.checked)} />
            Cap counted watch time at content duration
          </label>
        </div>

        <div className={style.formGroup} style={{ marginTop: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={!!form.enable_monetization}
              onChange={(e) => set("enable_monetization", e.target.checked)} />
            Monetization enabled (master switch)
          </label>
        </div>

        {/* ---------------------------------------------------------------
            Advanced — fraud review sensitivity. Collapsed by default so a
            normal settings change never runs into it.
        ---------------------------------------------------------------- */}
        <div className={style.advancedBlock}>
          <button
            type="button"
            className={style.advancedToggle}
            onClick={() => setShowAdvanced((v) => !v)}
            aria-expanded={showAdvanced}
          >
            <span className={style.advancedCaret}>{showAdvanced ? "▾" : "▸"}</span>
            Advanced — cheating checks
            <span className={style.advancedHint}>
              {showAdvanced ? "" : "Most people never need to change these"}
            </span>
          </button>

          {showAdvanced && (
            <div className={style.advancedBody}>
              <div className={style.noteBox}>
                <strong>These only decide who we take a look at.</strong> Nobody is stopped from
                watching and nobody's money changes. A bigger number means we look at fewer people.
              </div>

              <div className={style.formGrid}>
                {FRAUD_FIELDS.map((f) => (
                  <div className={style.formGroup} key={f.key}>
                    <label>{f.label}</label>
                    <input
                      className={style.input}
                      type="number"
                      min={f.min}
                      max={f.max}
                      step={f.step}
                      // Show the value in use rather than a ghost placeholder.
                      // Eight greyed-out boxes read as a broken form, and an
                      // admin cannot tune a number they cannot see.
                      value={form[f.key] ?? f.def}
                      onChange={(e) => set(f.key, e.target.value)}
                      title={`${f.help} Default ${f.def}, allowed ${f.min}–${f.max}.`}
                    />
                    <span className={style.helper}>{f.help}</span>
                  </div>
                ))}
              </div>

              <div className={style.formGroup} style={{ marginTop: 22 }}>
                <label style={{ fontWeight: 600 }}>Automatically stop paying for obvious cheating</label>
                <select
                  className={style.select}
                  style={{ maxWidth: 420 }}
                  value={form.watch_fraud_auto_exclude_mode || "shadow"}
                  onChange={(e) => set("watch_fraud_auto_exclude_mode", e.target.value)}
                >
                  <option value="shadow">Watch first — show me what it would stop (recommended)</option>
                  <option value="enforce">Stop paying for it automatically</option>
                  <option value="off">Do nothing</option>
                </select>
                <span className={style.helper} style={{ display: "block", lineHeight: 1.7, marginTop: 6 }}>
                  Applies only to the three things a real viewer cannot do by accident:{" "}
                  <strong>more hours than a day has</strong>,{" "}
                  <strong>more watching than their devices could produce</strong>, and{" "}
                  <strong>one computer pretending to be many devices</strong>. Everything else
                  always waits for a person to decide.
                </span>

                {/* Spelled out rather than implied. The dropdown labels only
                    describe the middle option clearly; without this a reader
                    reasonably assumes the default does nothing at all, when in
                    fact it measures — which is the whole reason to start there. */}
                <table className={style.modeTable}>
                  <thead>
                    <tr>
                      <th>Setting</th>
                      <th>Shows in the suspicious list?</th>
                      <th>Works out what to cut?</th>
                      <th>Actually cuts?</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className={(form.watch_fraud_auto_exclude_mode || "shadow") === "shadow" ? style.modeActive : undefined}>
                      <td>Watch first</td>
                      <td>Yes — all checks</td>
                      <td>Yes, those three</td>
                      <td><strong>No</strong></td>
                    </tr>
                    <tr className={form.watch_fraud_auto_exclude_mode === "enforce" ? style.modeActive : undefined}>
                      <td>Stop paying for it</td>
                      <td>Yes — all checks</td>
                      <td>Yes, those three</td>
                      <td><strong>Yes, those three</strong></td>
                    </tr>
                    <tr className={form.watch_fraud_auto_exclude_mode === "off" ? style.modeActive : undefined}>
                      <td>Do nothing</td>
                      <td>Yes — all checks</td>
                      <td>No</td>
                      <td>No</td>
                    </tr>
                  </tbody>
                </table>

                <span className={style.helper} style={{ display: "block", lineHeight: 1.7, marginTop: 8 }}>
                  Stay on <strong>Watch first</strong> for a month and look at what it says it would
                  have stopped. If those are real cheats and not big families, switch to stopping
                  it — with your own numbers behind the decision.
                </span>
              </div>

              <button
                type="button"
                className={style.linkBtn}
                onClick={() => {
                  // Clearing to empty makes the backend keep the stored value;
                  // sending the shipped default explicitly is what an admin
                  // actually means by "reset".
                  setForm((prev) => {
                    const next = { ...prev };
                    FRAUD_FIELDS.forEach((f) => { next[f.key] = f.def; });
                    return next;
                  });
                }}
              >
                Reset these to recommended defaults
              </button>
            </div>
          )}
        </div>

        <div className={style.formActions}>
          <button className={style.btn} onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
